import React from "react";
import { useHistory, useParams } from "react-router";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import { ConsumerDataCollectionForm } from "../../../../../components/organisms/collection-groups/ConsumerDataCollectionForm";
import { EmployeeDataCollectionForm } from "../../../../../components/organisms/collection-groups/EmployeeDataCollectionForm";
import {
  collectsCommercialInfoSlugById,
  EMPLOYEE_COLLECTION_PURPOSES_SURVEY,
} from "../../../../../components/organisms/collection-groups/EmploymentGroupPurposesQuestion";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../../../hooks";
import { useCollectionGroup } from "../../../../../hooks/useCollectionGroups";
import { RouteHelpers, Routes } from "../../../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";
import { Loading } from "../../../../../components";
import { useDataMapPendingCollectedPicIds } from "../../../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapCollectedPICIds } from "../../../../../../common/service/resources/data-map/dataMapTransformers";

type Params = {
  dataSubjectTypeId: UUIDString;
};

export const CollectedPicForGroupPage = () => {
  const history = useHistory();

  const { dataSubjectTypeId: collectionGroupId } = useParams<Params>();
  const orgId = usePrimaryOrganizationId();
  const [org, orgRequest] = usePrimaryOrganization();
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const [collectionGroup, collectionGroupsRequest] = useCollectionGroup(orgId, collectionGroupId);

  const {
    answers,
    setAnswer,
    mutators: { patchAnswer },
  } = useSurvey(
    orgId,
    collectionGroup ? `consumer-data-collection-${collectionGroup?.id}` : undefined,
  );

  const { answers: collectionAnswers } = useSurvey(orgId, EMPLOYEE_COLLECTION_PURPOSES_SURVEY);
  const collectionSlug = collectsCommercialInfoSlugById(collectionGroupId);

  const isEmploymentGroup = collectionGroup?.collectionGroupType === "EMPLOYMENT";

  const pending = useDataMapPendingCollectedPicIds(dataMap, {
    collectionGroupId: collectionGroup?.id,
  });
  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: collectionGroup?.id });

  const hasFinishedSurvey = isEmploymentGroup
    ? Boolean(collectionAnswers[collectionSlug])
    : Boolean(answers["inferences-done"]);

  if (!collectionGroup) {
    return <Loading />;
  }

  return (
    <StandardPageWrapper
      requests={[orgRequest, dataMapRequest, collectionGroupsRequest]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextContent={"Next"}
              nextLoading={patchAnswer.isLoading}
              nextDisabled={!hasFinishedSurvey || patchAnswer.isLoading}
              prevOnClick={() => history.push(Routes.dataMap.root)}
              nextOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.collectionGroup.workflow.dataRecipientAssociation(
                    collectionGroupId,
                  ),
                )
              }
            />
          }
        />
      }
    >
      <PageHeader titleContent={collectionGroup?.name} />

      {isEmploymentGroup ? (
        <EmployeeDataCollectionForm
          collected={collected}
          pending={pending}
          answers={answers}
          setAnswer={setAnswer}
          dataMap={dataMap}
          org={org}
          collectionGroup={collectionGroup}
        />
      ) : (
        collectionGroup && (
          <ConsumerDataCollectionForm
            answers={answers}
            setAnswer={setAnswer}
            dataMap={dataMap}
            org={org}
            collectionGroup={collectionGroup}
          />
        )
      )}
    </StandardPageWrapper>
  );
};
